import React from 'react';
import './About.css'; // We'll create this file next
import { Helmet } from 'react-helmet';


const About = () => {
  return (
    <div className="about-page-container">
      <Helmet>
        <title>About PC Build Guru – Our Philosophy & Recommendation Engine</title>
        <meta
          name="description"
          content="Learn about PC Build Guru's mission to simplify PC building with intelligent recommendations, budget optimization, and guaranteed compatibility."
        />
        <meta
          name="keywords"
          content="about pc build guru, pc build philosophy, custom pc recommendation engine, budget pc building, intelligent pc builder"
        />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://www.pcbuildguru.com/about" />

        {/* Open Graph */}
        <meta property="og:title" content="About PC Build Guru – Our Philosophy & Recommendation Engine" />
        <meta
          property="og:description"
          content="PC Build Guru replaces the confusion of PC building with expert guidance, budget optimization, and guaranteed compatibility."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.pcbuildguru.com/about" />
        <meta property="og:site_name" content="PC Build Guru" />
        <meta property="og:image" content="https://www.pcbuildguru.com/images/about-og-image.jpg" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="About PC Build Guru – Our Philosophy & Recommendation Engine" />
        <meta
          name="twitter:description"
          content="Simplifying PC building with intelligent recommendations and expert guidance."
        />
        <meta name="twitter:image" content="https://www.pcbuildguru.com/images/about-twitter-image.jpg" />

        {/* Structured Data: Organization */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Organization",
            "name": "PC Build Guru",
            "url": "https://www.pcbuildguru.com/",
            "logo": "https://www.pcbuildguru.com/logo.png",
            "sameAs": [
              "https://www.facebook.com/pcbuildguru",
              "https://www.twitter.com/pcbuildguru",
              "https://www.instagram.com/pcbuildguru",
              "https://www.linkedin.com/company/pcbuildguru"
            ],
            "description":
              "PC Build Guru helps you build your dream PC with intelligent recommendations, budget optimization, and guaranteed compatibility."
          })}
        </script>
      </Helmet>

      {/* --- Hero Section --- */}
      <section className="about-hero-section">
        <div className="about-hero-content">
          <h1 className="about-hero-headline">
            Building a PC Should Be Exciting, Not Exhausting.
          </h1>
          <p className="about-hero-subheadline">
            At MakeYourCustomPC, we believe that everyone deserves a computer perfectly suited to their needs. Our mission is to replace the confusion of PC building with the confidence of expert guidance.
          </p>
        </div>
      </section>

      {/* --- Our Philosophy Section --- */}
      <section className="philosophy-section">
        <h2 className="section-title-about">Our Guiding Philosophy</h2>
        <div className="philosophy-grid">
          <div className="philosophy-card">
            <div className="philosophy-icon">👑</div>
            <h3>Budget is King</h3>
            <p>Your budget is the most important rule. Our algorithm is designed to maximize every single rupee you spend, ensuring you get the best possible performance for your price point without compromise.</p>
          </div>
          <div className="philosophy-card">
            <div className="philosophy-icon">📈</div>
            <h3>Intelligent & Dynamic</h3>
            <p>Our system is smart. If you don't need an expensive feature like Wi-Fi, the money saved is automatically reallocated to what matters most—a faster graphics card or a more powerful processor.</p>
          </div>
          <div className="philosophy-card">
            <div className="philosophy-icon">✅</div>
            <h3>Guaranteed Results</h3>
            <p>Our algorithm will never leave you with an empty cart. If a specific feature isn't available in your budget, it gracefully falls back to find the next best compatible option, ensuring you always get a complete, working build.</p>
          </div>
        </div>
      </section>

      {/* --- How It Works Section --- */}
      <section className="how-it-works-section-about">
        <div className="how-it-works-content">
          <div className="how-it-works-text">
            <h2 className="section-title-about left-aligned">The Most Advanced Recommendation Engine</h2>
            <p>
              Building a PC is a puzzle with millions of possible combinations. Our team has spent countless hours building a sophisticated, multi-stage algorithm that does the hard work for you.
            </p>
            <p>
              It cross-references data from **thousands of individual components**—CPUs, motherboards, graphics cards, and more—checking for compatibility, performance benchmarks, and real-world pricing. It understands that a "Gaming" PC needs a powerful GPU, while a "Workstation" needs more CPU cores. This isn't just a parts list; it's an intelligent recommendation tailored to you.
            </p>
            <p>
              <strong>Our only goal is to make this complex process easy, so you can build with total confidence.</strong>
            </p>
          </div>
          <div className="how-it-works-image">
            <img src="https://placehold.co/600x450/207BFF/FFFFFF?text=Algorithm+Visualization" alt="Algorithm data flow" />
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;